import React, { ReactComponentElement, useMemo, useState } from 'react';
import {
    ButtonPrimary,
    Checkbox as DLSCheckbox,
    Heading,
    Input,
    Label,
    Select,
    SelectOption
} from '@americanexpress/dls-react';
import { Formik, FormikProps } from 'formik';
import useAsyncCallback from 'src/hooks/useAsyncCallback';
import AdminService, { ResetUserPayload } from 'src/services/admin.service';
import styles from './adminUserForm.module.scss';
import { FullUserModel } from 'src/types/user.model';
import Selectors from 'src/utils/selectors';
import LiteralsService from 'src/services/literals/LiteralsService';
import { useDeleteUserAttribute, useUpdateUserAttributes, useUserAttributes } from 'src/hooks/queries/users';
import { UserAttributes } from 'src/types/user-attributes.enum';
import StorageService from 'src/services/storage.service';

const DEMO_SNAPSHOTS = [
    'mentors',
    'blank',
    'demo',
    'demo_now',
    'demo_1st',
    'demob',
    'plaidsandbox',
    'delete_user'
] as const;

function Checkbox(props: {
    name: string;
    label: string;
    checked: boolean;
    onChange: (e: any) => void;
    'data-cy'?: string;
}): ReactComponentElement<any> {
    return (
        <DLSCheckbox
            id={props.name}
            name={props.name}
            label={props.label}
            checked={props.checked}
            onChange={props.onChange}
            data-cy={props['data-cy']}
        />
    );
}

export interface AdminUserProps {
    user: FullUserModel;
}
export function AdminUserForm(props: AdminUserProps): ReactComponentElement<any> {
    const user = props.user;
    const { isLoading: isLoadingUserAttributes, data: userAttributes } = useUserAttributes();
    const { mutate: updateAttributes } = useUpdateUserAttributes();
    const { mutate: deleteAttribute } = useDeleteUserAttribute();
    const [deleteUser, setDeleteUser] = useState<boolean>(false);
    const isTypeBudgeting = user?.defaultPlan.isTypeBudgeting;

    const initialValues: ResetUserPayload = useMemo(
        () =>
            user && {
                isTypeFull: user?.defaultPlan.isTypeFull,
                isTypeBudgeting,
                email: user?.email,
                snapshot: '',
                deleteUser,
                onboardingStep: '',
                termsVersion: user?.termsVersion ?? ''
            },
        [user, deleteUser]
    );

    const handleSubmit = useAsyncCallback(async (values: ResetUserPayload) => {
        await AdminService.resetUser(values);
    }, []);

    return (
        <div>
            <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
                {(props: FormikProps<ResetUserPayload>) => (
                    <form onSubmit={props.handleSubmit} className={styles.form}>
                        <div className={styles.section}>
                            <Heading level={2}>{LiteralsService.getLiteral('Admin.User.planTypeTitle')}</Heading>
                            <Checkbox
                                name="isTypeFull"
                                label={LiteralsService.getLiteral('Admin.User.fullPlanLabel')}
                                checked={props.values.isTypeFull}
                                onChange={props.handleChange}
                                data-cy={Selectors.Admin.FULL_MFP_CHECKBOX}
                            />
                            <Checkbox
                                name="isTypeBudgeting"
                                label={LiteralsService.getLiteral('Admin.User.budgetPlanLabel')}
                                checked={props.values.isTypeBudgeting}
                                onChange={props.handleChange}
                                data-cy={Selectors.Admin.SPENDSMART_CHECKBOX}
                            />
                        </div>
                        <div className={styles.section}>
                            <Heading level={3}>{LiteralsService.getLiteral('Admin.User.userAttributes')}</Heading>

                            <div className="flex flex-wrap" style={{ gap: '16px' }}>
                                <div>
                                    <b>
                                        <u>Current Attribute Values</u>
                                    </b>
                                    {isLoadingUserAttributes ? (
                                        <div>Loading...</div>
                                    ) : (
                                        <ul>
                                            {userAttributes &&
                                                Object.keys(userAttributes).map((key) => (
                                                    <li key={key}>
                                                        <b>{key}</b>: {userAttributes[key].toString()}
                                                    </li>
                                                ))}
                                        </ul>
                                    )}
                                </div>
                                <div className="flex flex-column" style={{ gap: '5px' }}>
                                    <b>
                                        <u>Update Actions</u>
                                    </b>
                                    <ButtonPrimary
                                        size="sm"
                                        onClick={() => updateAttributes({ isNewUser: true })}
                                        data-cy={Selectors.Admin.MAKE_ME_NEW_USER}
                                    >
                                        {LiteralsService.getLiteral('Admin.User.makeMeNewUser')}
                                    </ButtonPrimary>
                                    <ButtonPrimary
                                        size="sm"
                                        onClick={() => deleteAttribute(UserAttributes.IS_NEW_USER)}
                                        data-cy={Selectors.Admin.MAKE_ME_EXISTING_USER}
                                    >
                                        {LiteralsService.getLiteral('Admin.User.makeMeExistingUser')}
                                    </ButtonPrimary>
                                    <ButtonPrimary
                                        size="sm"
                                        onClick={() =>
                                            updateAttributes({
                                                [UserAttributes.TRANSACTION_CLASSIFICATION_WALKME_STARTUP_STEP]: 0
                                            })
                                        }
                                        data-cy={Selectors.Admin.RESET_TRANSACTION_CLASSIFICATION_WALKME}
                                    >
                                        {LiteralsService.getLiteral('Admin.User.resetTransactionClassificationWalkme')}
                                    </ButtonPrimary>
                                    <ButtonPrimary
                                        size="sm"
                                        onClick={() =>
                                            updateAttributes({
                                                [UserAttributes.TRANSACTION_CLASSIFICATION_WALKME_STARTUP_STEP]: 6
                                            })
                                        }
                                        data-cy={Selectors.Admin.MARK_DONE_TRANSACTION_CLASSIFICATION_WALKME}
                                    >
                                        {LiteralsService.getLiteral(
                                            'Admin.User.markDoneTransactionClassificationWalkme'
                                        )}
                                    </ButtonPrimary>
                                    <ButtonPrimary
                                        size="sm"
                                        onClick={() =>
                                            updateAttributes({ [UserAttributes.NEVER_SHOW_CREATE_RULE_PROMPT]: true })
                                        }
                                        data-cy={Selectors.Admin.NEVER_SHOW_CREATE_RULE_PROMPT}
                                    >
                                        {LiteralsService.getLiteral('Admin.User.neverShowCreateRulePrompt')}
                                    </ButtonPrimary>
                                    <ButtonPrimary
                                        size="sm"
                                        onClick={() => deleteAttribute(UserAttributes.NEVER_SHOW_CREATE_RULE_PROMPT)}
                                        data-cy={Selectors.Admin.SHOW_CREATE_RULE_PROMPT}
                                    >
                                        {LiteralsService.getLiteral('Admin.User.showCreateRulePrompt')}
                                    </ButtonPrimary>
                                    <ButtonPrimary size="sm" onClick={() => StorageService.setNative()}>
                                        {LiteralsService.getLiteral('Admin.User.makeMeNativeAppUser')}
                                    </ButtonPrimary>
                                    <ButtonPrimary size="sm" onClick={() => StorageService.removeNative()}>
                                        {LiteralsService.getLiteral('Admin.User.makeMeWebAppUser')}
                                    </ButtonPrimary>
                                </div>
                            </div>
                        </div>
                        <div className={styles.section}>
                            <div className={styles.buttonContainer}>
                                <ButtonPrimary
                                    onClick={async () => {
                                        await AdminService.forceHideUnhideAccounts();
                                    }}
                                >
                                    {LiteralsService.getLiteral('Admin.User.forceHideUnhideAccounts')}
                                </ButtonPrimary>
                            </div>
                            <div>
                                <Label htmlFor="email">{LiteralsService.getLiteral('Admin.User.emailLabel')}</Label>
                                <Input
                                    id="email"
                                    type="text"
                                    value={props.values.email}
                                    onChange={props.handleChange}
                                    data-cy={Selectors.General.EMAIL_INPUT}
                                />
                            </div>
                        </div>
                        <div className={styles.section}>
                            <Label htmlFor="snapshot">{LiteralsService.getLiteral('Admin.User.snapshotLabel')}</Label>
                            <Select
                                id="snapshot"
                                value={props.values.snapshot}
                                onChange={(e: any) => {
                                    if (e.target.value === 'delete_user') {
                                        setDeleteUser(true);
                                    }
                                    props.handleChange(e);
                                }}
                                data-cy={Selectors.Admin.DATA_SNAPSHOT_SELECT}
                            >
                                <SelectOption value="">
                                    {LiteralsService.getLiteral('Admin.User.unchangedSnapshot')}
                                </SelectOption>
                                {DEMO_SNAPSHOTS.map((snapshot) => (
                                    <SelectOption value={snapshot} key={snapshot}>
                                        {snapshot}
                                    </SelectOption>
                                ))}
                            </Select>
                        </div>

                        <div className={styles.section}>
                            <Label htmlFor="termsVersion">{LiteralsService.getLiteral('Admin.User.termsLabel')}</Label>
                            <Input
                                id="termsVersion"
                                type="number"
                                value={props.values.termsVersion}
                                onChange={props.handleChange}
                                data-cy={Selectors.Admin.TERMS_VERSION_INPUT}
                            />
                            <p>
                                <small>{LiteralsService.getLiteral('Admin.User.termsDescription')}</small>
                            </p>
                        </div>

                        <ButtonPrimary type="submit" data-cy={Selectors.Admin.ADMIN_SUBMIT_BUTTON}>
                            {LiteralsService.getLiteral('Admin.User.updateButton')}
                        </ButtonPrimary>

                        {!!props.values.snapshot && (
                            <p>
                                <strong>{LiteralsService.getLiteral('Admin.User.warning')}</strong>
                                {LiteralsService.getLiteral('Admin.User.warningMessage', [props.values.snapshot])}
                            </p>
                        )}
                    </form>
                )}
            </Formik>
        </div>
    );
}
